import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink, splitLink, createWSClient, wsLink } from '@trpc/client';
import type { AppRouter } from '@coaching/api/trpc/router.js';

/**
 * tRPC React hooks client.
 *
 * This creates typed React hooks for calling tRPC procedures.
 * The type parameter ensures full type safety across the client-server boundary.
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get the WebSocket URL based on current location.
 * Converts http:// to ws:// and https:// to wss://
 */
function getWebSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/api/trpc`;
}

/**
 * WebSocket client for tRPC subscriptions.
 * Created lazily to avoid issues during SSR or initial load.
 */
let wsClient: ReturnType<typeof createWSClient> | null = null;

function getWSClient() {
  if (!wsClient) {
    wsClient = createWSClient({
      url: getWebSocketUrl(),
      onClose: (cause) => {
        console.log('WebSocket closed:', cause);
      },
      onOpen: () => {
        console.log('WebSocket connected');
      },
    });
  }
  return wsClient;
}

/**
 * Creates the tRPC client configuration.
 *
 * Uses a splitLink to route:
 * - Subscriptions over WebSocket for real-time streaming
 * - Queries and mutations over HTTP batch link for efficiency
 */
export function createTRPCClient() {
  return trpc.createClient({
    links: [
      splitLink({
        // Route subscriptions to WebSocket, everything else to HTTP
        condition: (op) => op.type === 'subscription',
        true: wsLink({
          client: getWSClient(),
        }),
        false: httpBatchLink({
          url: '/api/trpc',
        }),
      }),
    ],
  });
}

export type { AppRouter };
